import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { AutoComplete, Empty, Input, Spin, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { coreApi } from "../../api/calls";
import moment from "moment";

/**
 * @component
 * @alias GlobalSearch
 * @property {object} employee
 * @returns view with matched date from Elastic
 */
const GlobalSearch = () => {
  const { t } = useTranslation();

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchTermMinLength = 3;

  useEffect(() => {
    if (searchTerm.length > searchTermMinLength) {
      const delayDebounceFn = setTimeout(() => {
        setLoading(true);

        // Send Axios request here
        coreApi
          .get("/search", { params: { term: searchTerm } })
          .then((response) => {
            setSearchResults(response.data);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  /**
   * Truncate a string to a given max length, appending ellipsis if needed.
   */
  const truncate = (str, max) =>
    str && str.length > max ? str.slice(0, max) + "…" : str;

  /**
   * Render title to the view.
   *
   * @param {string} title
   * @param {string} displayed
   * @param {string} total
   * @returns
   */
  const renderTitle = (title, displayed, total) => (
    <span>{title + " (" + displayed + t("common.outOfTotal") + total + t("common.resultsFound") + ")"}</span>
  );

  /**
   * Render item with links to the view.
   *
   * @param {object} result
   * @param {object} category
   * @returns
   */
  const renderItem = (result, category) => {
    const getDefaultDisplayValue = () => {
      if (category.model === "BuildingUnit") {
        const parts = [
          result?.name,
          result?.registration_number,
          result?.first_name && result?.last_name
            ? result.first_name + " " + result.last_name
            : null,
          result?.full_address
            ? result.full_address
            : result?.street && result?.house_number
              ? result.street + " " + result.house_number
              : null,
          result?.city,
          result?.company_name,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : result?.id ?? "undefined";
      }


      if (category.model === "Invoice") {
        const parts = [
          result?.invoice_number,
          result?.recipient_name ?? null,
          result?.total_amount != null
            ? result.total_amount + " " + (result?.currency_iso_4217 ?? "")
            : null,
        ].filter(Boolean);

        const displayValue = parts.length > 0 ? parts.join(", ") : result?.id ?? "undefined";
        const isTruncated = displayValue.length > 59;
        return isTruncated ? displayValue.slice(0, 58) + "…" : displayValue;
      }

      if (category.model === "Client") {
        const parts = [
          result?.name ? truncate(result.name, 35) : null,
          result?.billing_address_city,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : result?.id ?? "undefined";
      }

      // Build display value with phone if available
      let displayParts = [];
      
      // Name
      if (result?.first_name || result?.last_name) {
        displayParts.push([result?.first_name, result?.last_name].filter(Boolean).join(" "));
      } else if (result?.company_name) {
        displayParts.push(result.company_name);
      } else if (result?.registration_number) {
        displayParts.push(result.registration_number + (result.reference ? " " + result.reference : ""));
      } else if (result?.street && result?.house_number) {
        displayParts.push(result.street + " " + result.house_number);
      } else if (result?.email) {
        displayParts.push(result.email);
      } else if (result?.id) {
        displayParts.push(result.id);
      }
      
      // Add phone if exists (formatted)
      if (result?.phone) {
        displayParts.push("📞 " + result.phone);
      }
      
      return displayParts.length > 0 ? displayParts.join(" • ") : "undefined";
    };

    const getPartnerDisplayValue = () => {
      const fullName = [result?.first_name, result?.last_name].filter(Boolean).join(" ").trim();

      if (!fullName) {
        return getDefaultDisplayValue();
      }

      let parts = [fullName, result?.label, result?.city_resolved]
        .filter((item) => typeof item === "string" && item.trim() !== "");
      
      // Add phone if exists
      if (result?.phone) {
        parts.push("📞 " + result.phone);
      }
      
      return parts.join(" - ");
    };

    const displayValue = category.model === "Partner" ? getPartnerDisplayValue() : getDefaultDisplayValue();

    const getFullDisplayValue = () => {
      if (category.model === "Invoice") {
        const mainParts = [
          result?.invoice_number,
          result?.recipient_name,
          result?.total_amount != null
            ? result.total_amount + " " + (result?.currency_iso_4217 ?? "")
            : null,
        ].filter(Boolean).join(", ");

        const additionalParts = [
          result?.supplier_name ? `Dodavatel: ${result.supplier_name}` : null,
          result?.date_issued ? `Vystaveno: ${moment(result.date_issued).format('DD.MM.YYYY')}` : null,
          result?.date_due ? `Splatnost: ${moment(result.date_due).format('DD.MM.YYYY')}` : null,
          result?.date_paid ? `Zaplaceno: ${moment(result.date_paid).format('DD.MM.YYYY')}` : null,
          result?.invoice_type === 1 ? 'Typ: Partner→Klient' :
            result?.invoice_type === 2 ? 'Typ: Anna→Partner' :
              result?.invoice_type === 3 ? 'Typ: Partner→Anna' : null,
        ].filter(Boolean).join(" • ");

        return additionalParts ? `${mainParts} • ${additionalParts}` : mainParts;
      }
      if (category.model === "Client") {
        const address = [
          result?.billing_address_street,
          result?.billing_address_house_number,
        ].filter(Boolean).join(" ");

        const fullAddress = [
          address,
          result?.billing_address_city,
          result?.billing_address_postal_code,
        ].filter(Boolean).join(", ");

        return [
          result?.name,
          fullAddress,
          result?.company_number ? `IČO: ${result.company_number}` : null,
          result?.tax_identification_number ? `DIČ: ${result.tax_identification_number}` : null,
          result?.buildings?.length ? `Budovy: ${result.buildings.length}` : null,
          result?.balance != null ? `Zůstatek: ${result.balance} ${result?.currency_iso_4217 ?? 'CZK'}` : null,
        ].filter(Boolean).join(" • ");
      }

      if (category.model === "Contact") {
        return [
          result?.first_name && result?.last_name
            ? result.first_name + " " + result.last_name
            : result?.first_name || result?.last_name,
          result?.company_name ? `Firma: ${result.company_name}` : null,
          result?.phone ? `Tel: ${result.phone}` : null,
          result?.email ? `Email: ${result.email}` : null,
          result?.notes ? `${result.notes}` : null,
        ].filter(Boolean).join(" • ");
      }

      if (category.model === "Partner") {
        const fullName = [result?.first_name, result?.last_name].filter(Boolean).join(" ");

        return [
          fullName,
          result?.company_name ? `Firma: ${result.company_name}` : null,
          result?.label,
          result?.city_resolved,
          result?.phone ? `Tel: ${result.phone}` : null,
          result?.email ? `Email: ${result.email}` : null,
          result?.company_number ? `IČO: ${result.company_number}` : null,
          result?.vat_number ? `DIČ: ${result.vat_number}` : null,
        ].filter(Boolean).join(" • ");
      }

      return displayValue;
    };

    const titleValue = getFullDisplayValue();

    return {
      value: category.primary_key + result[category.primary_key],
      label: (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {category.model === "BuildingUnit" ? (
            <Tooltip title={titleValue} mouseEnterDelay={0.1}>
              <Link to={"/buildings/" + result.building_id + "/unit/" + result[category.primary_key]}>
                {displayValue}
              </Link>
            </Tooltip>
          ) : (
            <Tooltip title={titleValue} mouseEnterDelay={0.1}>
              <Link to={"/" + category.url + "/" + result[category.primary_key]}>
                {displayValue}
              </Link>
            </Tooltip>
          )}
        </div>
      )
    };
  };

  return (
    <AutoComplete
      dropdownMatchSelectWidth={437}
      style={{
        width: 200,
      }}
      options={searchResults.map((category) => {
        return {
          label: renderTitle(
            t("menu." + category.translation_key) || category.model,
            [...Object.values(category.results)].length,
            category.results_count
          ),
          options: [...Object.values(category.results)].map((result) => {
            return renderItem(result, category);
          }),
        };
      })}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: "center" }}>
            <Spin size={"large"}></Spin>
          </div>
        ) : (
          <Empty description={t("common.noSearchResults")}></Empty>
        )
      }
      value={searchValue}
      onChange={(value) => {
        setSearchValue(value);
      }}
      onSelect={() => {
        setSearchValue(searchTerm);
      }}
    >
      <Input
        readOnly={loading}
        allowClear
        placeholder={t("common.search")}
        suffix={loading ? <LoadingOutlined /> : false}
        prefix={
          <SearchOutlined
            className="site-form-item-icon"
            style={{ color: "rgba(0, 0, 0, 0.25)", marginRight: "2px" }}
          />
        }
        onChange={(e) => {
          if (e.target.value.length > searchTermMinLength) {
            setSearchTerm(e.target.value);
          } else {
            setSearchResults([]);
          }
        }}
      ></Input>
    </AutoComplete>
  );
};

export default GlobalSearch;
